<template>
  <div>
    <fullscreen-dialog
      hide-padding
      v-model="model"
      :title="menuDialogTitle"
      :subtitle="`ID: ${selectedDate.id}`"
      icon="mdi-silverware-fork-knife"
      color="primary"
      @save="saveMenu"
      @close="$emit('close')"
    >
      <template #filters>
        <menu-filters-bar
          :date.sync="selectedDate.date"
          :menu-name.sync="selectedDate.name"
          :filter-type.sync="selectedMealTypeFilter"
          :search.sync="searchQuery"
          :menu-id="selectedDate.id"
          :menu-types="menuTypes"
          :selected-shifts.sync="selectedShifts"
          @menu-type-change="handleMenuTypeChange"
          @add-meal="openMealDialog"
        />
      </template>

      <v-row class="mt-0">
        <v-col cols="12">
          <v-row v-if="filteredMeals.length > 0">
            <v-col cols="12" sm="4" xl="3" v-for="(item, i) in filteredMeals" :key="i">
              <meal-card
                :item="item"
                :images="galleryImages"
                @edit="openMealDialogForEdit"
                @order-changed="updateMealOrder"
                @reserve-changed="(value, item) => toggleReservableMeal(item)"
                @meal-deleted="removeMealFromList"
              />
            </v-col>
          </v-row>
          <div v-else class="empty-state">
            <v-row justify="center" class="py-8">
              <v-col cols="12" class="text-center">
                <v-icon size="80" color="grey lighten-1" class="mb-4">
                  mdi-silverware-fork-knife
                </v-icon>
                <h3 class="grey--text text--lighten-1 mb-2">
                  {{ hasActiveFilters ? $t("no_meals_found") : $t("no_meals_added") }}
                </h3>
                <p class="grey--text text--lighten-1">
                  {{
                    hasActiveFilters ? $t("try_different_search") : $t("add_first_meal")
                  }}
                </p>
                <primary-button
                  v-if="!hasActiveFilters || true"
                  @click="openMealDialog"
                  class="mt-4"
                >
                  <v-icon left>mdi-plus-circle</v-icon>
                  {{ $t("add_meals1") }}
                </primary-button>
              </v-col>
            </v-row>
          </div>
        </v-col>
      </v-row>

      <template #footer-left>
        <excel-button @click="exportMenuToExcel" class="mr-2"></excel-button>
        <print-button @click="printMenuDocument"></print-button>
      </template>
      <template #footer-actions>
        <v-spacer></v-spacer>

        <v-btn text color="error" @click="deleteMenu" class="mr-2">
          <v-icon left>mdi-delete</v-icon>
          {{ $t("delete") }}
        </v-btn>
        <v-btn text @click="openCopyDialog" class="mr-2">
          <v-icon left>mdi-content-copy</v-icon>
          {{ $t("copy") }}
        </v-btn>
        <v-btn color="primary" dark depressed large @click="saveMenu">
          <v-icon left>mdi-content-save</v-icon>
          {{ $t("save") }}
        </v-btn>
      </template>
    </fullscreen-dialog>

    <!-- FAB for adding meals -->
    <v-btn
      v-if="model"
      fab
      fixed
      bottom
      right
      color="primary"
      large
      class="meal-fab"
      @click="openMealDialog"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <menu-copy-dialog
      v-model="isCopyDialogOpen"
      :current-date="selectedDate.date"
      @copy="handleMenuCopy"
    />

    <meal-edit-dialog
      v-model="isMealEditDialogOpen"
      :foods="foodsByCategory"
      :menu-types="menuTypes"
      :edit-item="mealToEdit"
      :categories="categories"
      @add-meal="handleMealAdded"
      @update-meal="handleMealUpdated"
    ></meal-edit-dialog>

    <global-loader v-model="isLoading" />
    <global-confirm-box ref="confirmBox"></global-confirm-box>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";
import _debounce from "lodash/debounce";
import MealCard from "./MealCard.vue";
import MealEditDialog from "./MealEditDialog.vue";
import MenuCopyDialog from "./MenuCopyDialog.vue";
import MenuFiltersBar from "./MenuFiltersBar.vue";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";
import menuService from "@/services/menu";
import mealService from "@/services/meal";
import galleryService from "@/services/gallery";
import categoryService from "@/services/category";
import hasModel from "@/mixins/hasModel";

export default {
  mixins: [hasSnackbar, hasModel, hasPrinting, hasExcel],
  props: {
    selectedDate: { type: Object, default: false },
  },
  components: {
    MealCard,
    MealEditDialog,
    MenuCopyDialog,
    MenuFiltersBar,
    FullscreenDialog,
  },
  data() {
    return {
      menuTypes: [],
      categories: [],
      isCopyDialogOpen: false,
      selectedMealTypeFilter: "all",
      isMealEditDialogOpen: false,
      mealToEdit: null,
      foodsByCategory: {},
      galleryImages: [],
      searchQuery: "",
      isLoading: false,
      selectedShifts: [1, 2, 3, 4],
      isCopying: false,
    };
  },
  watch: {
    "selectedDate.date"() {
      if (!this.isCopying) {
        this.$emit("loadMenu", this.selectedDate.date);
      }
    },
  },
  created() {
    this.loadFoodsByCategory();
    this.loadCategories();
    this.loadGalleryImages();
    if (this.restaurant.settings.lice_bez_ugovora) {
      this.menuTypes = [{ name: this.$t("for_guest"), val: "guest_meal" }];
    }
    this.loadMenuTypes();
  },
  methods: {
    loadMenuTypes() {
      menuService
        .getMenuTypes()
        .then(({ data }) => {
          this.menuTypes = data;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_menu_types"));
        });
    },
    toggleReservableMeal(meal) {
      this.isLoading = true;
      menuService
        .changeReservedMeal({
          menu_id: this.selectedDate.id,
          meal_id: meal.can_reserve,
        })
        .then(() => {
          this.selectedDate.meals
            .filter((m) => m.id !== meal.id)
            .forEach((m) => (m.can_reserve = null));
          this.showSuccessSnackbar(this.$t("reserved_meal_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_updating_reserved_meal"));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    loadGalleryImages() {
      galleryService
        .getGalleryImages()
        .then((res) => {
          this.galleryImages = res.data;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_images"));
        });
    },
    updateMealOrder: _debounce(function (meal) {
      menuService
        .setOrderMeal(meal)
        .then(() => {
          this.showSuccessSnackbar(this.$t("order_updated"));
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("order_update_failed"));
        });
    }, 600),
    openCopyDialog() {
      this.isCopyDialogOpen = true;
    },
    handleMenuCopy(targetDate) {
      this.isCopying = true;
      const startOfDay = new Date(`${targetDate}T00:00:01`);
      const endOfDay = new Date(`${targetDate}T23:59:59`);
      const copiedMenu = {
        id: null, // Always null for copy operation
        name: this.selectedDate.name,
        date: targetDate,
        reserve_id: this.selectedDate.reserve_id,
        start: startOfDay,
        end: endOfDay,
        color: this.menuColor,
        timed: false,
        meals: this.selectedDate.meals,
        type: "rucak",
        one: this.selectedDate.one ? 1 : 0,
      };
      this.$emit("add", copiedMenu);
      this.loadGalleryImages();
      this.$nextTick(() => {
        this.isCopying = false;
      });
    },
    handleMenuTypeChange() {
      if (this.selectedDate.date && this.selectedDate.name) {
        menuService
          .getMenus({
            from: this.selectedDate.date,
            to: this.selectedDate.date,
            type: this.selectedDate.name,
          })
          .then((res) => {
            if (res.data.length) {
              this.$emit("select", res.data[0]);
            } else {
              this.$emit("select", {
                date: this.selectedDate.date,
                type: this.selectedDate.name,
                one: this.selectedDate.one ? 1 : 0,
                eating: [],
                meals: [],
              });
            }
          })
          .catch(() => {
            this.showErrorSnackbar(this.$t("error_loading_menu"));
          });
      }
    },
    removeMealFromList(mealId) {
      const index = this.selectedDate.meals.findIndex((meal) => meal.id === mealId);
      if (index !== -1) {
        this.selectedDate.meals.splice(index, 1);
      }
    },
    deleteMenu() {
      this.$refs.confirmBox
        .open(this.$t("are_you_sure"), this.$t("delete_menu_confirm"), {
          color: "error",
        })
        .then(() => {
          this.$emit("delete", this.selectedDate.id || null);
        })
        .catch((err) => console.warn(err));
    },
    loadCategories() {
      categoryService
        .getCategories()
        .then((res) => {
          this.categories = res.data || [];
        })
        .catch((error) => {
          console.error("Error loading categories:", error);
          this.showErrorSnackbar(this.$t("error_loading_categories"));
        });
    },
    loadFoodsByCategory() {
      mealService
        .getFoods()
        .then((res) => {
          let categorizedFoods = {};
          res.data.forEach((food) => {
            if (food && food.category && food.category.name) {
              if (!categorizedFoods.hasOwnProperty(food.category.name)) {
                categorizedFoods[food.category.name] = [];
              }
              categorizedFoods[food.category.name].push(food);
            }
          });
          let sortedCategories = Object.keys(categorizedFoods)
            .sort()
            .reduce((result, key) => ((result[key] = categorizedFoods[key]), result), {});
          for (let category in sortedCategories) {
            sortedCategories[category] = sortedCategories[category].sort((a, b) =>
              a.name.localeCompare(b.name)
            );
          }
          this.foodsByCategory = sortedCategories;
        })
        .catch(() => {
          this.showErrorSnackbar(this.$t("error_loading_meals"));
        });
    },
    openMealDialog() {
      this.mealToEdit = null;
      this.isMealEditDialogOpen = true;
    },
    openMealDialogForEdit(meal) {
      this.mealToEdit = meal;
      this.isMealEditDialogOpen = true;
    },
    saveMenu() {
      const date = this.selectedDate.date;
      const startOfDay = new Date(`${date}T00:00:01`);
      const endOfDay = new Date(`${date}T23:59:59`);
      const menuData = {
        id: this.selectedDate.id || null,
        name: this.selectedDate.name,
        date: date,
        reserve_id: this.selectedDate.reserve_id,
        start: startOfDay,
        end: endOfDay,
        color: this.menuColor,
        timed: false,
        meals: this.selectedDate.meals,
        type: "rucak",
        one: this.selectedDate.one ? 1 : 0,
      };
      this.$emit("add", menuData);
      this.loadGalleryImages();
    },
    handleMealAdded(mealData) {
      if (!this.selectedDate.hasOwnProperty("meals")) this.selectedDate.meals = [];
      this.selectedDate.meals.push(mealData);
      this.emitMenuUpdate();
    },
    handleMealUpdated(mealData) {
      if (this.mealToEdit) {
        const index = this.selectedDate.meals.findIndex(
          (meal) => meal.id === this.mealToEdit.id
        );
        if (index !== -1) {
          this.selectedDate.meals[index] = {
            ...this.selectedDate.meals[index],
            ...mealData,
            menu_id: this.selectedDate.id,
            type: mealData.type.filter(Boolean),
          };
        }
        this.emitMenuUpdate();
      }
    },
    emitMenuUpdate() {
      const date = this.selectedDate.date;
      const startOfDay = new Date(`${date}T00:00:01`);
      const endOfDay = new Date(`${date}T23:59:59`);
      this.$emit("add", {
        id: this.selectedDate.id || null,
        name: this.selectedDate.name,
        price: this.selectedDate.price,
        date: date,
        reserve_id: this.selectedDate.reserve_id,
        start: startOfDay,
        end: endOfDay,
        color: this.menuColor,
        timed: false,
        meals: this.selectedDate.meals,
        type: "rucak",
        one: this.selectedDate.one ? 1 : 0,
      });
    },
    getMealImageUrl(meal) {
      const storageUrl = meal.image?.host;
      const imageName = meal.image?.name;

      if (meal.clone_id != null && imageName && storageUrl) {
        return `${storageUrl}/gallery/${meal.clone_id}/medium/${imageName}`;
      }

      if (imageName && storageUrl) {
        return `${storageUrl}/gallery/${this.restaurant.id}/medium/${imageName}`;
      }

      return "./media/img/no-image.webp";
    },
    printMenuDocument() {
      const timestamp = this.localeDate(new Date(), {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
      const reportFooter = `<div style="margin-top:16px;font-size:14px;">${this.$t(
        "report_creation_datetime"
      )}: ${timestamp}</div>`;

      const tableHeaders = [
        { text: this.$t("r_b"), value: "num", width: "5%" },
        { text: this.$t("image"), value: "image", width: "15%", print: true },
        { text: this.$t("title"), value: "name", width: "25%" },
        { text: this.$t("menu_type"), value: "type", width: "15%" },
        { text: this.$t("shifts"), value: "shifts", width: "15%" },
      ];

      const printableRows = this.filteredMeals.map((meal, index) => {
        const mealShifts = [];
        if (meal.shift_one) mealShifts.push(this.$t("1st_shift"));
        if (meal.shift_two) mealShifts.push(this.$t("2nd_shift"));
        if (meal.shift_three) mealShifts.push(this.$t("3rd_shift"));
        if (meal.shift_four) mealShifts.push(this.$t("4th_shift"));

        const imageUrl = this.getMealImageUrl(meal);
        const imageHtml = `<img src="${imageUrl}" style="width: 80px; height: 60px; object-fit: cover; border-radius: 4px; border: 1px solid #ddd;" onerror="this.src='./media/img/no-image.webp'">`;

        return {
          num: index + 1,
          image: imageHtml,
          name: this.toLatin(meal.name || ""),
          description: meal.description || "",
          type: meal.type ? meal.type.map((type) => this.$t(type)).join(", ") : "",
          shifts: mealShifts.join(", "),
        };
      });

      this.printDocument({
        tables: [
          {
            headers: tableHeaders,
            items: printableRows,
          },
        ],
        hideDatetime: true,
        afterTableHtml: reportFooter,
        title: [this.$t("menu_for"), this.localeDate(this.selectedDate.date)].join(" "),
        customCSS: `
          <style>
            @media print {
              .print-table img {
                max-width: 80px !important;
                max-height: 60px !important;
                width: 80px !important;
                height: 60px !important;
                object-fit: cover;
                border-radius: 4px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.2);
              }
            }
          </style>
        `,
      });
    },
    exportMenuToExcel() {
      const timestamp = this.localeDate(new Date(), {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });

      const excelHeaders = [
        { text: this.$t("r_b"), value: "num", align: "center" },
        { text: this.$t("title"), value: "name", align: "start" },
        { text: this.$t("description"), value: "description", align: "start" },
        { text: this.$t("menu_type"), value: "type", align: "center" },
        { text: this.$t("shifts"), value: "shifts", align: "center" },
        { text: this.$t("price"), value: "price", align: "center" },
        { text: this.$t("quantity"), value: "quantity", align: "center" },
        { text: this.$t("unit_of_measure"), value: "measure", align: "center" },
      ];

      const excelRows = this.filteredMeals.map((meal, index) => {
        const mealShifts = [];
        if (meal.shift_one) mealShifts.push(this.$t("1st_shift"));
        if (meal.shift_two) mealShifts.push(this.$t("2nd_shift"));
        if (meal.shift_three) mealShifts.push(this.$t("3rd_shift"));
        if (meal.shift_four) mealShifts.push(this.$t("4th_shift"));

        return {
          num: index + 1,
          name: this.toLatin(meal.name || ""),
          description: this.translate(meal.description || ""),
          type: meal.type ? meal.type.map((type) => this.$t(type)).join(", ") : "",
          shifts: mealShifts.join(", "),
          price: meal.price
            ? `${meal.price} ${this.restaurant.settings.price || ""}`
            : "",
          quantity: meal.quantity || "",
          measure: meal.measure || "",
        };
      });

      const fileName = `jelovnik_${this.localeDate(
        this.selectedDate.date
      )}_${timestamp.replace(/[:.]/g, "-")}.xlsx`;

      this.downloadExcel({
        fileName,
        sheets: [
          {
            name: "Jelovnik",
            title: `${this.$t("menu_for")} ${this.localeDate(this.selectedDate.date)}`,
            subtitle: this.selectedDate.id ? `ID: ${this.selectedDate.id}` : "",
            beforeTableHtml: `${this.toLatin(
              this.toLatin(this.restaurant.name || "")
            )}<br>${this.toLatin(this.restaurant.address || "")}`,
            headers: excelHeaders,
            items: excelRows,
          },
        ],
      });
    },
  },
  computed: {
    restaurant() {
      return this.$store.state.restaurant;
    },
    menuDialogTitle() {
      const date = new Date(this.selectedDate.date);
      const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
      return [
        this.$t("routes.menu"),
        this.$t("for").toLowerCase(),
        this.localeDate(date, options),
      ].join(" ");
    },
    menuColor() {
      switch (this.selectedDate.name) {
        case "dorucak":
          return "green";
        case "rucak":
          return "blue";
        case "vecera":
          return "red";
        default:
          return "";
      }
    },
    filteredMeals() {
      if (!this.selectedDate.meals) return [];
      let meals = [...this.selectedDate.meals];

      // Filter by meal type
      if (this.selectedMealTypeFilter !== "all") {
        meals = meals.filter(
          (meal) => meal.type && meal.type.includes(this.selectedMealTypeFilter)
        );
      }

      // Filter by selected shifts
      if (this.selectedShifts && this.selectedShifts.length > 0) {
        meals = meals.filter((meal) => {
          const hasShift1 = this.selectedShifts.includes(1) && meal.shift_one;
          const hasShift2 = this.selectedShifts.includes(2) && meal.shift_two;
          const hasShift3 = this.selectedShifts.includes(3) && meal.shift_three;
          const hasShift4 = this.selectedShifts.includes(4) && meal.shift_four;
          return hasShift1 || hasShift2 || hasShift3 || hasShift4;
        });
      }

      // Ensure meal names are set from first food item
      meals.forEach((meal) => {
        if (!meal.name && meal.foods && meal.foods[0]) {
          meal.name = meal.foods[0].name;
        }
      });

      // Filter by search query
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        meals = meals.filter((meal) => {
          // Search in meal name
          const mealName = (meal.name || "").toLowerCase();
          if (mealName.includes(query)) return true;

          // Search in meal description
          const mealDescription = (meal.description || "").toLowerCase();
          if (mealDescription.includes(query)) return true;

          // Search in grocery names
          if (meal.foods && Array.isArray(meal.foods)) {
            const groceryMatch = meal.foods.some((food) => {
              const foodName = (food.name || "").toLowerCase();
              return foodName.includes(query);
            });
            if (groceryMatch) return true;
          }

          return false;
        });
      }

      return meals;
    },
    hasActiveFilters() {
      return (
        (this.searchQuery && this.searchQuery.trim()) ||
        this.selectedMealTypeFilter !== "all" ||
        (this.selectedShifts && this.selectedShifts.length < 4)
      );
    },
  },
};
</script>
<style>
.empty-state {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.empty-state .v-icon {
  opacity: 0.5;
}

.empty-state h3 {
  font-weight: 500;
}

.empty-state p {
  font-size: 16px;
  margin-bottom: 0;
}

.meal-fab {
  z-index: 1000 !important;
  margin-bottom: 80px !important; /* Above footer */
}

@media (max-width: 600px) {
  .meal-fab {
    margin-bottom: 70px !important;
  }
}
</style>
